/**
 * 🔴 VENDORED VERBATIM from RxConnect_BE `src/modules/messaging/message-segments.ts`.
 *
 * **Do not hand-edit.** Change it there and re-copy. The backend's count is what prices a send
 * against a pharmacy's caps and what the carrier is compared to; this copy is only what the
 * composer shows while somebody types. If the two disagree, the screen is what is wrong.
 *
 * A live counter cannot be served remotely — there is no "count these segments" route, and the send
 * route is throttled to 30/min — so the arithmetic has to exist on both sides. Kept honest by
 * `message-segments.test.ts`, whose vectors are the backend spec's own.
 * (Precedent: `packages/shared/src/telemetry/`, vendored from RXAdmin_BE the same way.)
 *
 * ── original header follows ───────────────────────────────────────────────────────────────────
 *
 * How many SMS segments a text costs, and in which encoding (Track MSG · MSG2 · docs/92 §3.4).
 *
 * **Pure — no IO.** Used to price a send before it happens (caps, the composer's live counter) and
 * checked against the carrier's own `parts` once it has been submitted.
 *
 * ## The two encodings
 *
 * - **GSM-7**: 160 septets in one segment, 153 per segment once split. The characters in the
 *   extension table (`^ { } \ [ ] ~ | €` and form feed) cost **two** septets each.
 * - **UCS-2**: anything outside GSM-7 switches the WHOLE message to UCS-2 — 70 UTF-16 code units in
 *   one segment, 67 per segment once split. One `ê` in a French text does it (measured live
 *   2026-09-22: an 89-character French notice went out as 2 UCS-2 parts, `docs/test/fixtures/msg1`).
 *
 * 🔴 This is an **estimate**, and says so. The carrier's `parts` is the authoritative count and
 * replaces this one on the message row once known. The two can differ by one in a rare edge (an
 * extension character or a surrogate pair straddling a split point); nothing bills from the estimate.
 */

/** GSM 03.38 basic character set (the escape character itself is not a user character). */
const GSM7_BASIC = new Set(
  Array.from(
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡' +
      'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
  ),
);

/** GSM 03.38 extension table — each costs an escape plus the character: two septets. */
const GSM7_EXTENSION = new Set(Array.from('^{}\\[~]|€\f'));

export type SegmentEncoding = 'gsm7' | 'ucs2';

export interface SegmentCount {
  encoding: SegmentEncoding;
  /** Septets (GSM-7) or UTF-16 code units (UCS-2) — the unit the carrier counts in. */
  units: number;
  segments: number;
}

/** The carrier refuses anything longer than this many parts. */
export const MAX_CARRIER_SEGMENTS = 10;

const LIMITS: Record<SegmentEncoding, { single: number; multi: number }> = {
  gsm7: { single: 160, multi: 153 },
  ucs2: { single: 70, multi: 67 },
};

export function countSegments(text: string): SegmentCount {
  let septets = 0;
  let gsm7 = true;
  for (const ch of Array.from(text)) {
    if (GSM7_BASIC.has(ch)) {
      septets += 1;
    } else if (GSM7_EXTENSION.has(ch)) {
      septets += 2;
    } else {
      gsm7 = false;
      break;
    }
  }
  const encoding: SegmentEncoding = gsm7 ? 'gsm7' : 'ucs2';
  // UCS-2 counts UTF-16 code units, which is exactly what `String#length` returns.
  const units = gsm7 ? septets : text.length;
  const { single, multi } = LIMITS[encoding];
  const segments = units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi);
  return { encoding, units, segments };
}

/**
 * The longest a staff-typed text may be, from RxConnect_BE `message-content-validator.ts`
 * (`MAX_MANUAL_SEGMENTS`). Vendored here so the composer can stop somebody at the limit instead of
 * letting the server refuse a message they have already finished writing.
 *
 * ⚠️ This one is policy, not arithmetic — it is the number most likely to move. The backend
 * re-validates every send regardless; this is only what the composer draws.
 */
export const MAX_MANUAL_SEGMENTS = 4;
